import React, { useState, useEffect, useCallback } from 'react';
import { Button, Col, Row, Form as BootstrapForm } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { WebApiCalls } from '../../webApiCalls/WebApiCalls';
import AddressInputWithMapClick from '../Modals/VendorModals/AddressInputWithMapClick';

const validate = Yup.object({
    name: Yup.string().required('Branch name is required'),
    address: Yup.string().required('Address is required'),
    location: Yup.object({
        lat: Yup.number().required('Latitude is required'),
        lng: Yup.number().required('Longitude is required')
    })
});

const AddEditBranch = () => {
    const { state } = useLocation(); // Access the state object
    const [status, setStatus] = useState('active');
    const navigate = useNavigate();
    const { getToken } = useAuth();
    const [branchDetails, setBranchDetails] = useState(null);
    const [allBranches, setAllBranches] = useState([]);

    const fetchBranchDetails = useCallback(
        async (savedState) => {
            const token = getToken();
            const branchId = state?.branch_id || savedState?.branch_id;

            if (token && branchId) {
                const res = await new WebApiCalls().getSingleBranch(token, branchId);
                if (res?.code === 200) {
                    setBranchDetails(res.data);
                    setStatus(res.data.status);
                } else {
                    toast.error('Failed to fetch branch details.');
                }
            }
        },
        [getToken, state]
    );

    useEffect(() => {
        // Persist state to local storage if available
        if (state) {
            localStorage.setItem('branchState', JSON.stringify(state));
        }
    }, [state]);

    useEffect(() => {
        // Retrieve state from local storage if not available in useLocation
        const savedState = localStorage.getItem('branchState');
        const parsedState = savedState ? JSON.parse(savedState) : null;

        if (state?.branch_id && state?.branch_detail) {
            setBranchDetails(state.branch_detail); // Populate branch details for editing
        } else if (parsedState?.branch_id && parsedState?.branch_detail) {
            setBranchDetails(parsedState.branch_detail);
        } else {
            fetchBranchDetails(parsedState);
        }
    }, [state, fetchBranchDetails]);

    useEffect(() => {
        return () => {
            // Clear local storage state when navigating away
            localStorage.removeItem('branchState');
        };
    }, []);

    useEffect(() => {
        // Fetch all branches for parent selection
        const fetchBranches = async () => {
            const token = getToken();
            if (token) {
                const res = await new WebApiCalls().getAllBranches(token, 1000, 0, '', '');
                if (res?.code === 200) {
                    setAllBranches(res?.data?.branches || []);
                }
            }
        };
        fetchBranches();
    }, [getToken]);

    const handleSubmit = async (values) => {
        const token = getToken();
        if (!token) return;

        const apiCall = state?.branch_id
            ? new WebApiCalls().updateBranch(token, state?.branch_id, { ...values, status })
            : new WebApiCalls().createBranch(token, { ...values, status });

        const res = await apiCall;
        if (res?.code === 200) {
            toast.success(`Branch ${state?.branch_id ? 'updated' : 'created'} successfully.`);
            navigate('/branches');
        } else {
            toast.error(`Failed to ${state?.branch_id ? 'update' : 'create'} branch.`);
        }
    };

    return (
        <Row className="d-flex justify-content-center">
            <Col xs={12} sm={12} md={11} lg={11} className="shadow-lg p-5 m-3">
                <Formik
                    initialValues={{
                        name: branchDetails?.name || '',
                        address: branchDetails?.address || '',
                        location: {
                            lat: branchDetails?.lat || '',
                            lng: branchDetails?.lng || ''
                        },
                        parent: branchDetails?.parent?._id || ''
                    }}
                    validationSchema={validate}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {(formik) => (
                        <Form>
                            <Row>
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                    <BootstrapForm.Group>
                                        <BootstrapForm.Label>Branch Name</BootstrapForm.Label>
                                        <BootstrapForm.Control type="text" name="name" placeholder="Enter branch name" {...formik.getFieldProps('name')} />
                                        {formik.errors.name && <div className="text-danger">{formik.errors.name}</div>}
                                    </BootstrapForm.Group>
                                </Col>
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                    <BootstrapForm.Group>
                                        <BootstrapForm.Label>Status</BootstrapForm.Label>
                                        <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </BootstrapForm.Group>
                                </Col>
                                <Col xs={12} sm={12} md={6} lg={6} className="mb-2">
                                    <BootstrapForm.Group>
                                        <BootstrapForm.Label>Main Branch</BootstrapForm.Label>
                                        <select className="form-control" name="parent" value={formik.values.parent} onChange={formik.handleChange}>
                                            <option value="">None</option>
                                            {allBranches
                                                .filter((b) => !branchDetails || b._id !== branchDetails._id) // Prevent self-parenting
                                                .map((b) => (
                                                    <option key={b._id} value={b._id}>
                                                        {b.name}
                                                    </option>
                                                ))}
                                        </select>
                                    </BootstrapForm.Group>
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={12} className="mb-3">
                                    <div className="config-form-container px-0">
                                        <label className="config-legend-label">Location</label>
                                        <AddressInputWithMapClick
                                            errors={formik.errors}
                                            values={formik.values}
                                            setFieldValue={formik.setFieldValue}
                                            label="Branch Address"
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <div className="text-end">
                                <Link to="/branches">
                                    <Button type="button" className="theme-cancel-btn me-3">
                                        Cancel
                                    </Button>
                                </Link>
                                <Button type="submit" className="theme-btn">
                                    {state?.branch_id ? 'Update' : 'Save'}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </Col>
        </Row>
    );
};

export default AddEditBranch;
